import { ArrowDownIcon, ArrowUpIcon } from 'components/icons';
import { cn } from 'utils/cn';

type Props = {
    label: string;
    /** The figure as it should read, already formatted. */
    value: string;
    /** A line under the figure: the period it covers or how it is computed. */
    caption?: string;
    /** A change in percent: draws an arrow beside the figure, up for a gain and down for a loss. */
    change?: number | null;
};

/** One key figure of a report: its name, the figure in the figure size and an optional caption. */
export function KpiTile({ label, value, caption, change }: Props) {
    const direction = change == null || change === 0 ? null : change > 0 ? 'up' : 'down';
    return (
        <div className="bg-card ring-foreground/10 flex min-w-0 flex-col gap-1 rounded-xl px-5 py-4 ring-1">
            <span className="text-caption text-muted-foreground font-medium">{label}</span>
            <span className="flex items-center gap-2">
                <span className="text-figure text-foreground not-new:tracking-normal tabular-nums">{value || '–'}</span>
                {direction && (
                    <span
                        className={cn(
                            'inline-flex size-6 items-center justify-center rounded-full',
                            direction === 'up' ? 'bg-success-tint text-success-ink' : 'bg-danger-tint text-danger-ink'
                        )}
                    >
                        {direction === 'up' ? (
                            <ArrowUpIcon className="size-3.5" />
                        ) : (
                            <ArrowDownIcon className="size-3.5" />
                        )}
                    </span>
                )}
            </span>
            {caption && <span className="text-caption text-muted-foreground">{caption}</span>}
        </div>
    );
}

/** A row of key figures that wraps on a narrow screen. */
export function KpiRow({ children }: { children: React.ReactNode }) {
    return (
        <section className="new:gap-(--card-gap) grid grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-3">
            {children}
        </section>
    );
}
